import React from 'react';
import {
  // Función para crear el sistema del editor
  createEditorSystem,
  // Componente visual del editor
  RichText,
  // Extensiones
  boldExtension,
  italicExtension,
  underlineExtension,
  historyExtension,
  blockFormatExtension
} from '@lexkit/editor';
import { PlainlyFormat } from './types';
import { Toolbar } from './toolbar';
import { PlainlyOutputPlugin } from './plugins/plainly-output';
import { LineLimitPlugin } from './plugins/line-limit-plugin';
import './editor.css';

interface RichTextEditorProps {
  placeholder?: string;
  initialValue?: string;
  onChange: (value: PlainlyFormat) => void;
  onBlur?: () => void;
  hasError?: boolean;
  disabled?: boolean;
  maxLines?: number; // Límite de líneas (default: 3)
}

const extensions = [
  boldExtension,
  italicExtension,
  underlineExtension,
  historyExtension,
  blockFormatExtension
] as const;

// Crear el sistema del editor con las extensiones definidas
const { Provider, useEditor } = createEditorSystem<typeof extensions>();

export { useEditor };

// Componente principal que se exporta y se usa en RichTextField
export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  placeholder = 'Ingresá un texto',
  initialValue = '',
  onChange,
  onBlur,
  hasError = false,
  disabled = false,
  maxLines = 3
}) => {
  return (
    /* Configuración del Provider del sistema del editor  */
    <Provider
      extensions={extensions}
      config={{
        editable: !disabled, // read-only
        theme: {
          text: {
            bold: 'rich-text-bold',
            italic: 'rich-text-italic',
            underline: 'rich-text-underline'
          },
          paragraph: 'rich-text-paragraph'
        },
        onError: (error: Error) => {
          console.error('Lexical error:', error);
        }
      }}
    >
      {/* Container principal */}
      <div className={`rich-text-editor-container ${hasError ? 'has-error' : ''} ${disabled ? 'disabled' : ''}`}>
        {/* Toolbar */}
        <Toolbar />

        {/* Contenedor del editor - Wrap del RichText para poder agregar onBlur */}
        <div className='rich-text-editor' onBlur={onBlur}>
          <RichText
            placeholder={placeholder}
            classNames={{
              contentEditable: 'rich-text-content',
              placeholder: 'rich-text-placeholder'
            }}
          />
          {/* Plugins */}
          <PlainlyOutputPlugin onChange={onChange} />
          <LineLimitPlugin maxLines={maxLines} />
        </div>
      </div>
    </Provider>
  );
};
